import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useSectionNav } from "@/lib/scroll";
import { NAV_LINKS } from "@/data/site";

const EASE = [0.16, 1, 0.3, 1];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const go = useSectionNav();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const isActive = (path) => (path === "/" ? pathname === "/" : pathname.startsWith(path));

  return (
    <>
      <motion.header
        initial={{ y: -24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
        data-testid="navigation-header"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled ? "bg-[#0a0a0b]/75 backdrop-blur-xl border-b border-white/5" : "bg-transparent border-b border-transparent"
        }`}
      >
        <div
          className={`max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between transition-[height] duration-500 ${
            scrolled ? "h-16" : "h-20"
          }`}
        >
          <button data-testid="nav-home-logo" onClick={() => go("/")} className="shrink-0" aria-label="Novo Digital Studio — home">
            <Logo compact={scrolled} />
          </button>
          <nav className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <button
                key={l.path}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                onClick={() => go(l.path)}
                className={`relative mono text-[11px] tracking-[0.22em] uppercase transition-colors duration-300 ${
                  isActive(l.path) ? "text-neutral-50" : "text-neutral-400 hover:text-neutral-50"
                }`}
              >
                {l.label}
                {isActive(l.path) && (
                  <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-neutral-50" aria-hidden="true" />
                )}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <button
              data-testid="nav-quote-button"
              onClick={() => go("/contact")}
              className="group hidden sm:inline-flex items-center gap-2 rounded-full bg-neutral-50 text-[#0a0a0b] text-sm font-medium pl-5 pr-4 py-2.5 hover:bg-white transition-colors duration-300"
            >
              Get a Quote
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5" />
            </button>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen(!open)}
              className="md:hidden text-neutral-200 p-2"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 bg-[#0a0a0b]/95 backdrop-blur-xl md:hidden flex flex-col justify-center px-8"
          >
            <nav className="flex flex-col">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.path}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: EASE }}
                  onClick={() => go(l.path)}
                  className="text-left font-display text-4xl font-bold text-neutral-100 py-4 border-b border-white/5"
                >
                  {l.label}
                </motion.button>
              ))}
            </nav>
            <motion.button
              data-testid="mobile-quote-button"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5, ease: EASE }}
              onClick={() => go("/contact")}
              className="group mt-10 inline-flex items-center justify-center gap-2 rounded-full bg-neutral-50 text-[#0a0a0b] font-medium px-7 py-4"
            >
              Get a Quote
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
